import React from "react";
import { Routes, Route } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

import "./App.css";
import { UserContext } from "./misc/UserContext";
import LandingPage from "./LandingPage/LandingPage";
import Dashboard from "./Dashboard/Dashboard";
import { IDispatchAction, IUserState } from "./types";
import AppContainer from "./ui/AppContainer/AppContainer";
import Datasets from "./Dataset/Datasets";
import DatasetCVM from "./Dataset/DatasetCVM";
import { getLocalStorage } from "./api";
import Relationships from "./Relationship/Relationships";
import RelationshipCVM from "./Relationship/RelationshipCVM";
import Reconsets from "./Reconset/Reconsets";
import ReconsetCVM from "./Reconset/ReconsetCVM";
import Reconset from "./Reconset/View/Reconset";
import Status from "./Reconset/View/Status";
import Match from "./Reconset/View/Match";
import Loaders from "./Loader/Loaders";
import LoaderCVM from "./Loader/LoaderCVM";
import Loader from "./Loader/Loader";
import FunctionCVM from "./Function/FunctionCVM";
import Functions from "./Function/Functions";
import UserCVM from "./User/UserCVM";
import Users from "./User/Users";
import Roles from "./Role/Roles";
import RoleCVM from "./Role/RoleCVM";
import PasswordReset from "./User/PasswordReset";
import Task from "./Task/Task";
import Tasks from "./Task/Tasks";
import MatchTemp from "./Reconset/View/Temp/MatchTemp";
import Adapters from "./Adapter/Adapters";
import AdapterCVM from "./Adapter/AdapterCVM";
import UseCaseCVM from "./UseCase/UseCaseCVM";
import UseCases from "./UseCase/UseCases";
import UseCaseView from "./UseCase/UseCaseView";
import UseCaseMatch from "./UseCase/View/Match/Match";
import UseCaseStatus from "./UseCase/View/Status/Status";
import UseCaseEvent from "./UseCase/View/Event/Event";
import PolicyCVM from "./AccessControl/Policy/PolicyCVM";
import QuerySetCVM from "./features/QuerySet/QuerySetCVM";
import QuerySets from "./features/QuerySet/QuerySets";

type actionTypes =
  | "SET-SUCCESS"
  | "SET-ERROR"
  | "SET-RECONSET-ENTITY-ID"
  | "SET-RECONSET-ID"
  | "SET-RECONSET-RUN"
  | "SET-RECONSET-QUERY-COLUMNS-VALUES"
  | "SET-RECONSET-QUERY-COLUMNS-LOVS"
  | "SET-RECONSET-QUERY-COLUMNS"
  | "SET-RECONSET-QUERY-COLUMNS-VALUE"
  | "SET-RECONSET-QUERY-COLUMNS-LOV"
  | "SET-RECONSET-RUN-ID"
  | "SET-USECASE-ENTITY-ID"
  | "SET-USECASE-ID"
  | "SET-USECASE-RUN-ID"
  | "SET-USECASE-RUN-ID-ACCESS";

const initialState: IUserState = {
  successMessage: "",
  errorMessage: "",
  user: getLocalStorage("user"),
  currentEntity: getLocalStorage("currentEntity"),
  reconset: {
    entityId: "",
    id: "",
    queryColumns: [],
    queryColumnsValue: [],
    queryColumnsLOV: [],
  },
  useCase: {
    entityId: "",
    id: "",
    runId: "",
    access: "*",
  },
};

function reducer(state: IUserState, action: IDispatchAction<actionTypes>) {
  switch (action.type) {
    case "SET-SUCCESS":
      return { ...state, successMessage: action.value };
    case "SET-ERROR":
      return { ...state, errorMessage: action.value };
    case "SET-RECONSET-ENTITY-ID":
      return {
        ...state,
        reconset: { ...state.reconset, entityId: action.value },
      };
    case "SET-RECONSET-ID":
      return { ...state, reconset: { ...state.reconset, id: action.value } };
    case "SET-RECONSET-QUERY-COLUMNS-VALUES":
      return {
        ...state,
        reconset: { ...state.reconset, queryColumnsValue: action.value },
      };
    case "SET-RECONSET-QUERY-COLUMNS-VALUE": {
      let valueIndex = state.reconset.queryColumnsValue.findIndex((column) => column.name === action.value.name);

      if (valueIndex >= 0) {
        const newQueryColumnsColumnValues = state.reconset.queryColumnsValue.map((column) => {
          if (column.name === action.value.name) {
            return {
              name: column.name,
              value: action.value.value,
            };
          }
          return column;
        });

        return {
          ...state,
          reconset: {
            ...state.reconset,
            queryColumnsValue: newQueryColumnsColumnValues,
          },
        };
      } else {
        const newQueryColumnsColumnValues = [...state.reconset.queryColumnsValue];
        newQueryColumnsColumnValues.push(action.value);
        return {
          ...state,
          reconset: {
            ...state.reconset,
            queryColumnsValue: newQueryColumnsColumnValues,
          },
        };
      }
    }
    case "SET-RECONSET-QUERY-COLUMNS-LOV": {
      let valueIndex = state.reconset.queryColumnsLOV.findIndex((column) => column.name === action.value.name);

      if (valueIndex >= 0) {
        const newQueryColumnsColumnValues = state.reconset.queryColumnsLOV.map((column) => {
          if (column.name === action.value.name) {
            return {
              name: column.name,
              value: action.value.value,
            };
          }
          return column;
        });

        return {
          ...state,
          reconset: {
            ...state.reconset,
            queryColumnsLOV: newQueryColumnsColumnValues,
          },
        };
      } else {
        const newQueryColumnsColumnValues = [...state.reconset.queryColumnsLOV];
        newQueryColumnsColumnValues.push(action.value);
        return {
          ...state,
          reconset: {
            ...state.reconset,
            queryColumnsLOV: newQueryColumnsColumnValues,
          },
        };
      }
    }
    case "SET-USECASE-ENTITY-ID":
      return {
        ...state,
        useCase: { ...state.useCase, entityId: action.value },
      };
    case "SET-USECASE-ID":
      return { ...state, useCase: { ...state.useCase, id: action.value } };
    case "SET-USECASE-RUN-ID":
      return { ...state, useCase: { ...state.useCase, runId: action.value } };
    case "SET-USECASE-RUN-ID-ACCESS":
      return {
        ...state,
        useCase: { ...state.useCase, access: action.value || "*" },
      };
    default:
      return state;
  }
}

const queryClient = new QueryClient();

function App() {
  const [state, dispatch] = React.useReducer(reducer, initialState);

  return (
    <UserContext.Provider value={{ state: state, dispatch: dispatch }}>
      <DndProvider backend={HTML5Backend}>
        <QueryClientProvider client={queryClient}>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route
              path="/app/dashboard"
              element={<AppContainer component={Dashboard} action="dashboard" validate={false} header="Dashboard" />}
            />

            <Route path="/passwordreset" element={<PasswordReset />} />

            <Route path="/app/datasets">
              <Route
                path=""
                element={
                  <AppContainer
                    component={Datasets}
                    action={["dataset:definition:view", "dataset:definition:create", "dataset:definition:edit"]}
                    header="Datasets"
                  />
                }
              />
              <Route
                path="/app/datasets/new"
                element={
                  <AppContainer component={DatasetCVM} action="dataset:definition:create" header="New Dataset" />
                }
              />
              <Route
                path="/app/datasets/edit"
                element={<AppContainer component={DatasetCVM} action="dataset:definition:edit" header="Edit Dataset" />}
              />
            </Route>

            <Route path="/app/relationships">
              <Route
                path=""
                element={
                  <AppContainer
                    component={Relationships}
                    action={[
                      "relationship:definition:view",
                      "relationship:definition:create",
                      "relationship:definition:edit",
                    ]}
                    header="Relationships"
                  />
                }
              />
              <Route
                path="/app/relationships/new"
                element={
                  <AppContainer
                    component={RelationshipCVM}
                    action="relationship:definition:create"
                    header="New Relationship"
                  />
                }
              />
              <Route
                path="/app/relationships/edit"
                element={
                  <AppContainer
                    component={RelationshipCVM}
                    action="relationship:definition:edit"
                    header="Edit Relationship"
                  />
                }
              />
            </Route>

            <Route path="/app/reconsets">
              <Route
                path=""
                element={
                  <AppContainer
                    component={Reconsets}
                    action={[
                      "reconset:definition:view",
                      "reconset:definition:create",
                      "reconset:definition:edit",
                      "reconset:run:view",
                    ]}
                    header="Reconsets"
                  />
                }
              />
              <Route
                path="/app/reconsets/new"
                element={
                  <AppContainer component={ReconsetCVM} action="reconset:definition:create" header="New Reconset" />
                }
              />
              <Route
                path="/app/reconsets/edit"
                element={
                  <AppContainer component={ReconsetCVM} action="reconset:definition:edit" header="Edit Reconset" />
                }
              />
              <Route path="/app/reconsets/view">
                <Route
                  path="/app/reconsets/view"
                  element={<AppContainer component={Reconset} action="reconset:run:view" header="Reconset" />}
                />

                <Route
                  path="/app/reconsets/view/status"
                  element={<AppContainer component={Status} action="reconset:run:view" header="Reconset" />}
                />

                <Route
                  path="/app/reconsets/view/match"
                  element={<AppContainer component={Match} action="reconset:run:view" header="Reconset" />}
                />
                <Route
                  path="/app/reconsets/view/temp"
                  element={<AppContainer component={MatchTemp} action="reconset:run:view" header="Reconset" />}
                />
              </Route>
            </Route>

            <Route path="/app/usecases">
              <Route
                path=""
                element={
                  <AppContainer
                    component={UseCases}
                    action={[
                      "usecase:definition:view",
                      "usecase:definition:create",
                      "usecase:definition:edit",
                      "usecase:run:view",
                    ]}
                    header="Use Cases"
                    type="MONITORING"
                  />
                }
              />
              <Route
                path="/app/usecases/new"
                element={
                  <AppContainer component={UseCaseCVM} action="usecase:definition:create" header="New Use Case" />
                }
              />
              <Route
                path="/app/usecases/edit"
                element={
                  <AppContainer component={UseCaseCVM} action="usecase:definition:edit" header="Edit Use Case" />
                }
              />
              <Route path="/app/usecases/view">
                <Route
                  path="/app/usecases/view"
                  element={<AppContainer component={UseCaseView} action="usecase:run:view" header="Use Case" />}
                />

                <Route
                  path="/app/usecases/view/status"
                  element={<AppContainer component={UseCaseStatus} action="usecase:run:view" header="Use Case" />}
                />

                <Route
                  path="/app/usecases/view/match"
                  element={<AppContainer component={UseCaseMatch} action="usecase:run:view" header="Use Case" />}
                />

                <Route
                  path="/app/usecases/view/event"
                  element={<AppContainer component={UseCaseEvent} action="usecase:run:view" header="Use Case" />}
                />

                <Route
                  path="/app/usecases/view/temp"
                  element={<AppContainer component={MatchTemp} action="usecase:run:view" header="Use Case" />}
                />
              </Route>
            </Route>

            <Route path="/app/querysets">
              <Route
                path=""
                element={
                  <AppContainer
                    component={QuerySets}
                    action={["queryset:definition:view", "queryset:definition:create", "queryset:definition:edit"]}
                    header="Query Sets"
                    type="QUERYSET"
                  />
                }
              />
              <Route
                path="/app/querysets/new"
                element={
                  <AppContainer component={QuerySetCVM} action="queryset:definition:create" header="New Query Set" />
                }
              />

              <Route
                path="/app/querysets/edit"
                element={
                  <AppContainer component={QuerySetCVM} action="queryset:definition:edit" header="Edit Query Set" />
                }
              />
            </Route>

            <Route path="/app/loaders">
              <Route
                path=""
                element={
                  <AppContainer
                    component={Adapters}
                    action={[
                      "loader:definition:view",
                      "loader:definition:create",
                      "loader:definition:edit",
                      "loader:run:view",
                    ]}
                    header="Loaders"
                    type="LOADER"
                  />
                }
              />
              <Route
                path="/app/loaders/new"
                element={<AppContainer component={LoaderCVM} action="loader:definition:create" header="New Loader" />}
              />
              <Route
                path="/app/loaders/edit"
                element={<AppContainer component={LoaderCVM} action="loader:definition:edit" header="Edit Loader" />}
              />

              <Route
                path="/app/loaders/view"
                element={<AppContainer component={Loader} action="loader:run:view" header="Loader" />}
              />
            </Route>

            {/*             <Route path="/app/adapters">
              <Route
                path=""
                element={
                  <AppContainer
                    component={Adapters}
                    action={["adapter:definition:view", "adapter:definition:edit"]}
                    header="Loaders"
                  />
                }
              />
              <Route
                path="/app/adapters/new"
                element={<AppContainer component={AdapterCVM} action="adapter:definition:create" header="New Loader" />}
              />
              <Route
                path="/app/adapters/edit"
                element={<AppContainer component={AdapterCVM} action="adapter:definition:edit" header="Edit Loader" />}
              />
            </Route>
 */}
            <Route path="/app/functions">
              <Route
                path=""
                element={
                  <AppContainer
                    component={Functions}
                    action={["function:definition:view", "function:definition:create", "function:definition:edit"]}
                    header="Functions"
                  />
                }
              />
              <Route
                path="/app/functions/new"
                element={
                  <AppContainer component={FunctionCVM} action="function:definition:create" header="New Function" />
                }
              />
              <Route
                path="/app/functions/edit"
                element={
                  <AppContainer component={FunctionCVM} action="function:definition:edit" header="Edit Function" />
                }
              />
            </Route>

            <Route path="/app/users">
              <Route
                path=""
                element={
                  <AppContainer
                    component={Users}
                    action={["user:definition:view", "user:definition:create", "user:definition:edit"]}
                    header="Users"
                  />
                }
              />
              <Route
                path="/app/users/new"
                element={<AppContainer component={UserCVM} action="user:definition:create" header="New User" />}
              />
              <Route
                path="/app/users/edit"
                element={<AppContainer component={UserCVM} action="user:definition:edit" header="Edit User" />}
              />
            </Route>

            <Route path="/app/policies">
              <Route
                path=""
                element={
                  <AppContainer
                    component={Adapters}
                    action={["policy:definition:view", "policy:definition:create", "policy:definition:edit"]}
                    header="Policies"
                    type="POLICY"
                  />
                }
              />
              <Route
                path="/app/policies/new"
                element={<AppContainer component={PolicyCVM} action="policy:definition:create" header="New Policy" />}
              />
              <Route
                path="/app/policies/edit"
                element={<AppContainer component={PolicyCVM} action="policy:definition:edit" header="Edit Policy" />}
              />
            </Route>

            <Route path="/app/roles">
              <Route
                path=""
                element={
                  <AppContainer
                    component={Roles}
                    action={["role:definition:view", "role:definition:create", "role:definition:edit"]}
                    header="Roles"
                  />
                }
              />
              <Route
                path="/app/roles/new"
                element={<AppContainer component={RoleCVM} action="role:definition:create" header="New Role" />}
              />
              <Route
                path="/app/roles/edit"
                element={<AppContainer component={RoleCVM} action="role:definition:edit" header="Edit Role" />}
              />
            </Route>

            <Route path="/app/tasks">
              <Route
                path=""
                element={<AppContainer component={Tasks} action="tasks" validate={false} header="Tasks" />}
              />
              <Route
                path="/app/tasks/view"
                element={<AppContainer component={Task} action="tasks" validate={false} header="Tasks" />}
              />
            </Route>
          </Routes>
        </QueryClientProvider>
      </DndProvider>
    </UserContext.Provider>
  );
}

export default App;
